
import { Component, Vue } from 'vue-property-decorator'
import { Form as ElForm } from 'element-ui'
import axios from 'axios'

interface Feature {
  id?: number
  featureKey: string
  name: string
  description: string
  isActive: boolean
  configJson?: {
    type?: string
    deviceRequired?: boolean
    requiresV2?: boolean
    [key: string]: any
  }
  createdAt?: Date
  updatedAt?: Date
}

@Component({
  name: 'FeatureManagement'
})
export default class extends Vue {
  private list: Feature[] = []
  private listLoading = true
  private dialogFormVisible = false
  private dialogViewVisible = false
  private dialogStatus = ''
  private formData: any = {
    featureKey: '',
    name: '',
    description: '',
    type: 'permanent',
    deviceRequired: false,
    requiresV2: false,
    isActive: true,
    defaultDurationDays: 365,
    reminderDays: [30, 7, 1],
    platforms: ['ios', 'android'],
    configJsonStr: ''
  }
  private viewData: Feature = {
    featureKey: '',
    name: '',
    description: '',
    isActive: false
  }
  private rules = {
    featureKey: [{ required: true, message: 'Feature key is required', trigger: 'blur' }],
    name: [{ required: true, message: 'Name is required', trigger: 'blur' }],
    type: [{ required: true, message: 'Type is required', trigger: 'change' }]
  }

  created() {
    this.getList()
  }

  private get dialogTitle() {
    return this.dialogStatus === 'create' ? 'Create Feature' : 'Edit Feature'
  }

  private async getList() {
    this.listLoading = true
    try {
      const response = await axios.get(`${process.env.VUE_APP_BASE_API}/admin/features`)
      if (response.data.success) {
        this.list = response.data.data
      }
    } catch (error) {
      this.$message.error('Failed to load features')
      console.error(error)
    } finally {
      this.listLoading = false
    }
  }

  private resetFormData() {
    this.formData = {
      featureKey: '',
      name: '',
      description: '',
      type: 'permanent',
      deviceRequired: false,
      requiresV2: false,
      isActive: true,
      defaultDurationDays: 365,
      reminderDays: [30, 7, 1],
      platforms: ['ios', 'android'],
      configJsonStr: ''
    }
  }

  private handleCreate() {
    this.resetFormData()
    this.dialogStatus = 'create'
    this.dialogFormVisible = true
    this.$nextTick(() => {
      (this.$refs.dataForm as ElForm).clearValidate()
    })
  }

  private handleEdit(row: Feature) {
    this.formData = {
      id: row.id,
      featureKey: row.featureKey,
      name: row.name,
      description: row.description,
      type: row.configJson?.type || 'permanent',
      deviceRequired: row.configJson?.deviceRequired || false,
      requiresV2: row.configJson?.requiresV2 || false,
      isActive: row.isActive,
      defaultDurationDays: row.configJson?.defaultDurationDays || 365,
      reminderDays: row.configJson?.reminderDays || [30, 7, 1],
      platforms: row.configJson?.platforms || ['ios', 'android'],
      configJsonStr: ''
    }
    this.dialogStatus = 'update'
    this.dialogFormVisible = true
    this.dialogViewVisible = false
    this.$nextTick(() => {
      (this.$refs.dataForm as ElForm).clearValidate()
    })
  }

  private handleView(row: Feature) {
    this.viewData = { ...row }
    this.dialogViewVisible = true
  }

  private async saveData() {
    (this.$refs.dataForm as ElForm).validate(async(valid: boolean) => {
      if (valid) {
        try {
          // Build configJson from form fields
          let configJson: any = {
            type: this.formData.type,
            deviceRequired: this.formData.deviceRequired,
            requiresV2: this.formData.requiresV2,
            platforms: this.formData.platforms
          }

          // Add duration and reminder settings for time-based features
          if (this.formData.type === 'time_limited' || this.formData.type === 'subscription') {
            configJson.defaultDurationDays = this.formData.defaultDurationDays
            configJson.reminderDays = this.formData.reminderDays
          }

          // Merge with custom JSON if provided (for advanced settings)
          if (this.formData.configJsonStr && this.formData.configJsonStr.trim()) {
            try {
              const customConfig = JSON.parse(this.formData.configJsonStr)
              configJson = { ...configJson, ...customConfig }
            } catch (e) {
              this.$message.error('Invalid JSON in Advanced Settings field')
              return
            }
          }

          const data = {
            featureKey: this.formData.featureKey,
            name: this.formData.name,
            description: this.formData.description,
            isActive: this.formData.isActive,
            configJson
          }

          if (this.dialogStatus === 'create') {
            await axios.post(`${process.env.VUE_APP_BASE_API}/admin/features`, data)
            this.$message.success('Feature created successfully')
          } else {
            await axios.put(`${process.env.VUE_APP_BASE_API}/admin/features/${this.formData.id}`, data)
            this.$message.success('Feature updated successfully')
          }

          this.dialogFormVisible = false
          this.getList()
        } catch (error: any) {
          this.$message.error(error.response?.data?.error || 'Operation failed')
          console.error(error)
        }
      }
    })
  }

  private async handleStatusChange(row: Feature) {
    try {
      await axios.put(`${process.env.VUE_APP_BASE_API}/admin/features/${row.id}`, {
        isActive: row.isActive
      })
      this.$message.success('Status updated successfully')
    } catch (error) {
      this.$message.error('Failed to update status')
      // Revert the switch
      row.isActive = !row.isActive
      console.error(error)
    }
  }

  private getFeatureTagType(featureKey: string): string {
    const typeMap: { [key: string]: string } = {
      'voice_assistant': 'warning',
      'smart_map': 'primary',
      'weassist': 'info',
      'navigation_plus': 'primary',
      'referral_bonus': 'danger'
    }
    return typeMap[featureKey] || ''
  }

  private getTypeLabel(type: string | undefined): string {
    const typeLabels: { [key: string]: string } = {
      'permanent': 'Permanent',
      'time_limited': 'Time Limited',
      'subscription': 'Subscription',
      'consumable': 'Consumable'
    }
    return type ? typeLabels[type] || type : 'N/A'
  }

  private formatDate(date: Date | undefined): string {
    if (!date) return 'N/A'
    const d = new Date(date)
    const day = d.getDate().toString().padStart(2, '0')
    const month = (d.getMonth() + 1).toString().padStart(2, '0')
    const year = d.getFullYear()
    const hours = d.getHours().toString().padStart(2, '0')
    const minutes = d.getMinutes().toString().padStart(2, '0')
    return `${day}.${month}.${year} ${hours}:${minutes}`
  }
}
